import React, { useEffect, useRef, useState } from "react"

import OrderTable from "./UI/FoodTableOrderTable";
import OrderForm from "./UI/OrderForm";
import withOrder from "./withOrder";
import PaymentMessage from "./UI/PaymentMessage"
import EftposPanel from "./UI/EftposPanel"
import useEftposPayment from "./UI/useEftposPayment"
import { paneEnabled, panelView } from "../eftpos/panelView"
import { createPayButton } from "../eftpos/panelActions"
import { checkoutLock, offeredPaymentTypes } from "../eftpos/checkoutLock"

const FoodTableOrderForm = props => {
  // WDCE-312/WDCE-311: must reflect the order's actual persisted delivery fee, not
  // hardcode false - a false default made FoodTableOrderTable's fee-sync effect treat
  // every delivery order as "just unchecked," auto-PATCHing delivery_required: 0 on
  // mere page load, which (post-WDCE-311) also destroys the real delivery_address.
  const posDeliveryRequired = parseFloat((props.order.deliveryFee || '').replace(/[^0-9.]/g, '')) > 0
  const [deliveryRequired, setDeliveryRequired] = useState(props.order.orderType == 'pos' ? posDeliveryRequired : true)
  // WDCE-295: seeded once from props.order.status, then advanced locally on a
  // successful process-order PATCH - props.order itself is never refetched or
  // mutated after mount (withOrder only fetches once), and deliberately left
  // untouched here: props.order.status only ever feeds the initial spread into
  // OrderTable, while `editable` below (which does drive quantity/delivery
  // read-only state, symmetrically for both paid and shipped - WDCE-320) is
  // computed from this separately-tracked orderStatus state instead. The two
  // diverging after a successful "Process Order" transition is expected and
  // harmless: editable already treats paid and shipped identically.
  const [orderStatus, setOrderStatus] = useState(props.order.status)
  const [processingOrder, setProcessingOrder] = useState(false)

  // WDCE-316: distinguishes "is this the editable pre-payment page" from
  // "is delivery required" - the two used to be conflated via deliveryRequired
  // alone, which meant the delivery-fee-override control (and the auto-sync
  // effect that can PATCH it) started appearing/firing on the paid/shipped
  // Thank You page the moment deliveryRequired stopped being hardcoded false.
  const editable = !['paid', 'shipped'].includes(orderStatus)

  // WDCE-199 step 9g-3b (D56): the EFTPOS panel, only on an open POS order whose payment types include EFTPOS (the
  // server lists it only for someone it's offered to, step 9h-3) to someone who may take it. Otherwise it's off: no
  // session, no request, and OrderForm is given no panel.
  const eftposEnabled = paneEnabled({
    editable, canPayByEftpos: props.canPayByEftpos, paymentTypes: props.order.paymentTypes
  })
  const eftpos = useEftposPayment({ enabled: eftposEnabled, orderId: props.order.id })
  // OrderForm's "Process order and print packing slip" checkbox, kept here so the EFTPOS start can send it too. It
  // starts from the order's saved value only while the panel is on, and unticked otherwise, as before.
  const [processAndPrint, setProcessAndPrint] = useState(
    () => eftposEnabled && props.order.processAndPrintRequested === true
  )
  const [checkingOut, setCheckingOut] = useState(false)
  const [pressPay] = useState(() => createPayButton({ onCheckingOut: setCheckingOut }))
  const eftposView = eftposEnabled ? panelView({
    state: eftpos.state,
    keyBusy: eftpos.keyBusy,
    checkingOut,
    approvedHere: eftpos.approvedHere,
    eftposTerminal: props.eftposTerminal,
    canPayByEftpos: props.canPayByEftpos,
    eftposAdminPaymentsPath: props.eftposAdminPaymentsPath
  }) : null
  // WDCE-199 step 9g-4b (D56): while the panel is active, or the checkout is being saved, the rest of the checkout is
  // locked, with a line saying why; once part is paid, only EFTPOS is offered. With the panel off: no lock (OrderForm is
  // given no locked prop) and the payment types as they are.
  const lock = checkoutLock({ enabled: eftposEnabled, state: eftpos.state, view: eftposView, checkingOut })
  const lockProps = lock.lockable ? { locked: lock.locked } : {}
  const paymentTypesOffered = offeredPaymentTypes(props.order.paymentTypes, {
    enabled: eftposEnabled, eftposApproved: eftpos.state.eftposApproved
  })

  const payByEftpos = () => pressPay({
    view: eftposView,
    handleOrderUpdate: props.handleOrderUpdate,
    pay: eftpos.pay,
    eftposTerminal: props.eftposTerminal,
    processAndPrint,
    alert: (message) => window.alert(message),
    reload: () => window.location.reload()
  })

  // Fully paid by a payment this page was following: onto the paid page, as a cash checkout does. Once.
  const reloading = useRef(false)
  const reloadForPayment = Boolean(eftposView && eftposView.reload)
  useEffect(() => {
    if (!reloadForPayment || reloading.current) return
    reloading.current = true
    window.location.reload()
  }, [reloadForPayment])

  const packingSlipPath = `/pos/orders/${props.order.id}/packing_slip.pdf`

  const processOrder = () => {
    setProcessingOrder(true)
    // Open synchronously in the click handler so the browser doesn't treat it as
    // a popup - location gets set once the PATCH resolves, below.
    const pdfWindow = window.open('', '_blank')

    const body = new FormData()
    body.append("order[status]", "shipped")

    fetch(`/orders/${props.order.id}`, {
      body: body,
      method: "PATCH",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").getAttribute("content"),
      },
    })
      .then(response => response.json())
      .then(response => {
        if (response.order && response.order.status == 'shipped') {
          setOrderStatus('shipped')
          pdfWindow.location = packingSlipPath
        } else {
          pdfWindow.close()
          alert('Unable to process this order. Please try again.')
        }
      })
      .catch(() => {
        pdfWindow.close()
        alert('Unable to process this order. Please try again.')
      })
      .finally(() => setProcessingOrder(false))
  }

  const handleDeliveryRequiredChange = (required) => {
    setDeliveryRequired(required);
  };

  if (!editable) {
    return (
      <div>
        <PaymentMessage>
          <h3>Thank you for your order</h3>
          <p>Your payment has been successfully processed.</p>
        </PaymentMessage>

        <h2 className="text-center">Order</h2>
        <OrderTable deliveryRequired={deliveryRequired} editable={editable} {...props.order}/>

        <div className="form-actions">
          {orderStatus == 'shipped' ? (
            <a
              className="btn btn-primary btn-lg text-uppercase"
              href={packingSlipPath}
              target="_blank"
              rel="noopener noreferrer"
            >
              View / Print Packing Slip
            </a>
          ) : (
            <button
              className="btn btn-primary btn-lg text-uppercase"
              onClick={processOrder}
              disabled={processingOrder}
            >
              {processingOrder ? 'Processing...' : 'Process Order & Print Packing Slip'}
            </button>
          )}
        </div>
      </div>)
  } else {
    return (
      <OrderForm
        order={props.order}
        handleOrderUpdate={props.handleOrderUpdate}
        onDeliveryRequiredChange={handleDeliveryRequiredChange}
        {...props}
        processAndPrint={{ checked: processAndPrint, onChange: setProcessAndPrint }}
        eftposPanel={eftposView && <EftposPanel view={eftposView} onPay={payByEftpos} onKey={eftpos.pressKey} />}
        eftposActive={Boolean(eftposView && eftposView.active)}
        {...lockProps}
        offeredPaymentTypes={paymentTypesOffered}
      >
        {lock.message && <div className="alert alert-warning">{lock.message}</div>}
        <h2 className="text-center">Your Order</h2>
        <OrderTable
          handleOrderUpdate={props.handleOrderUpdate}
          deliveryRequired={deliveryRequired}
          editable={editable}
          {...props.order}
          locked={lock.locked}
        />
      </OrderForm>
    )
  }
}

export default withOrder(FoodTableOrderForm)
